import React, { useState } from 'react';
import styles from './SampleReportCard.module.css';

/* ── Severity color map ── */
const SEV = {
  CRITICAL: { bg: 'rgba(220, 38, 38, 0.12)', border: 'rgba(220, 38, 38, 0.25)', color: '#ef4444' },
  HIGH:     { bg: 'rgba(245, 158, 11, 0.12)', border: 'rgba(245, 158, 11, 0.25)', color: '#f59e0b' },
  MEDIUM:   { bg: 'rgba(196, 137, 59, 0.12)',  border: 'rgba(196, 137, 59, 0.25)',  color: '#C4893B' },
  LOW:      { bg: 'rgba(91, 127, 166, 0.12)',  border: 'rgba(91, 127, 166, 0.25)',  color: '#5B7FA6' },
};

/* ── Reports Data ── */
const REPORTS = [
  {
    id: 'kult',
    appName: 'Kult - Makeup/Beauty Shopping',
    appMeta: 'com.kult.app',
    videoSrc: './screen_recordings/kult.mp4',
    videoDur: '2 min 26 sec',
    pdfName: 'Kult.pdf',
    pdfLink: './sample_reports/Kult.pdf',
    specs: [
      ['Device', 'INFINIX Infinix X6870'],
      ['OS', 'Android 15'],
      ['Network', '4G'],
      ['RAM', '8.0 GB'],
      ['Screen', '1080×2436'],
      ['Tier', 'HIGH'],
      ['Location', 'Hubballi, Karnataka'],
      ['Tester', 'Verified Crowd Tester'],
      ['Session', '146s'],
      ['Speed', '43 Mbps'],
      ['Net Drop', 'No'],
      ['Thermal', 'Severe'],
      ['Free RAM', '2491 MB'],
      ['Storage', '72043 MB'],
      ['Battery Saver', 'Off'],
    ],
    repro: [
      'Launch the Kult - Makeup/Beauty Shopping app.',
      'Tap "Get Started" on the welcome screen.',
      'On the "Enter your phone number to begin" screen, enter a valid 10-digit mobile number.',
      'Tap "Continue".',
      'Observe the "Enter 6-digit code we just sent you" screen with the "Resend OTP in X" countdown.',
      'Wait for the OTP to arrive; it does not. (Optional: Pull down notification shade or open Messages app to verify non-receipt.)',
      'Allow the timer to expire or repeatedly attempt to re-enter the phone number and request a new OTP. No OTP will be received.'
    ],
    rootCause: `The primary issue, the non-delivery of OTPs, strongly suggests a problem with the backend's OTP generation and/or SMS gateway integration. Despite the device having active mobile data (43 Mbps), the OTP messages are not received. This could be due to: Failure of the backend service to send the SMS, issues with the SMS gateway provider (e.g., throttling, invalid number formatting), or an incorrect client-side permission. The UI glitches during screen transitions ([Frame 65], [Frame 145]) and in the app switcher thumbnail indicate rendering issues potentially related to how Android handles app state or fragment/activity lifecycle transitions on this specific budget Android device and app version.`,
    fixes: [
      'Investigate and debug the OTP generation and SMS sending service on the backend. Verify SMS gateway logs for delivery failures.',
      'Review client-side (Android) OTP handling logic to ensure correct SMS permissions are requested and granted.',
      'Address UI transition glitches by reviewing the Android layout and activity/fragment transition animations. Ensure proper lifecycle management.',
      'Optimize app rendering in the background or during state changes to prevent graphical corruption in the app switcher thumbnail.',
      'Examine the implementation of the OTP countdown timer to ensure it is running on the UI thread without blocking.'
    ],
    issues: [
      {
        frame: '[Frame 10-144] 0:09',
        title: 'OTP Non-Delivery Blocking Login',
        desc: 'The OTP entry screen is displayed with a countdown timer. The tester repeatedly waits for an OTP, checks the device\'s Messages app, and initiates new OTP requests. No OTP from Kult is observed.',
        impact: 'User is completely blocked from accessing the application, rendering the app unusable.',
        severity: 'CRITICAL',
      },
      {
        frame: '[Frame 65] 1:02',
        title: 'Overlapping UI during screen transition',
        desc: 'During the transition from the "Enter your phone number" screen to the "Enter 6-digit code" screen, elements from both screens are visually overlaid, including a stuck loading spinner.',
        impact: 'Visual glitch creating a confusing and unprofessional user experience during a critical authentication step.',
        severity: 'MEDIUM',
      },
      {
        frame: '[Frame 49] 0:47',
        title: 'Corrupted thumbnail in App Switcher',
        desc: 'When the app is displayed as a thumbnail in the Android app switcher, its visual rendering is pixelated and corrupted. Text is blurry.',
        impact: 'Poor visual presentation of the app in the system UI.',
        severity: 'LOW',
      },
      {
        frame: '[Frame 72] 1:09',
        title: 'Countdown timer frozen between frames',
        desc: 'The "Resend OTP in X" countdown timer does not decrement between consecutive 1-second sampled frames.',
        impact: 'Inconsistent countdown timer does not provide accurate real-time feedback to the user.',
        severity: 'LOW',
      }
    ]
  },
  {
    id: 'huft',
    appName: 'Heads Up For Tails',
    appMeta: 'com.headsupfortails.app',
    videoSrc: './screen_recordings/heads_up_for_tails.mp4',
    videoDur: '5 min 13 sec',
    pdfName: 'Heads_up_for_tails.pdf',
    pdfLink: './sample_reports/Heads_up_for_tails.pdf',
    specs: [
      ['Device', 'INFINIX Infinix X6870'],
      ['OS', 'Android 15'],
      ['Network', '4G'],
      ['RAM', '8.0 GB'],
      ['Screen', '1080×2436'],
      ['Tier', 'HIGH'],
      ['Location', 'Hubballi, Karnataka'],
      ['Tester', 'Verified Crowd Tester'],
      ['Session', '313s'],
      ['Speed', '1048 Mbps'],
      ['Net Drop', 'No'],
      ['Thermal', 'Severe'],
      ['Free RAM', '1198 MB'],
      ['Storage', '14991 MB'],
      ['Battery Saver', 'Off'],
    ],
    repro: [
      'Launch the "Heads Up For Tails" app.',
      'Navigate to the "Category" tab at the bottom.',
      'Tap the "Dogs" tab if not already selected.',
      'Scroll down and tap on "Supplements" subcategory.',
      'On Dog Supplements listing, rapidly scroll down for several seconds.',
      'Observe blurry images, rendering artifacts, and UI state inconsistencies.',
    ],
    rootCause: `The highest thermal state of "Severe" (39.1°C) combined with slow and blurry image loading during fast scrolling points to inefficient image handling. Likely cause: unoptimized image caching leading to memory leaks or excessive CPU/GPU load. When confronted with high volumes of images from rapid scrolling, the app overloads system resources, causing the device to overheat, which exacerbates UI rendering artifacts and state inconsistencies.`,
    fixes: [
      'Implement robust image lazy loading with adaptive quality and aggressive memory management.',
      'Optimize image assets server-side to provide web-optimized sizes and formats for mobile.',
      'Profile CPU, GPU, and memory usage during rapid scrolling sessions to identify bottlenecks.',
      'Refactor UI state management and navigation logic to prevent incorrect screens and persistent UI elements.',
      'Stress test image-heavy sections on limited hardware while monitoring memory, CPU, and thermal metrics.',
    ],
    issues: [
      { frame: '[Frame 55-63] 1:08', title: 'Context menu stuck on screen for 10+ seconds', desc: 'A "Copy Share Select all" context menu appears after a long-press and persists on the FAQ screen.', impact: 'UI obstruction. Prevents interaction with elements underneath.', severity: 'HIGH' },
      { frame: '[Frame 41-46] 0:50', title: 'FAQ screen skeleton loading for 8 seconds', desc: 'The FAQ screen shows grey placeholder boxes for 6 frames. Content only loads at Frame 47.', impact: 'Delayed content rendering. User waits with no feedback.', severity: 'MEDIUM' },
      { frame: '[Frame 70] 1:27', title: 'Wrong content on Return & Exchange page', desc: 'The Return & Exchange Policy page displays FAQ content instead of the actual policy.', impact: 'Incorrect content shown. Misinformation and navigational confusion.', severity: 'MEDIUM' },
      { frame: '[Frame 158-159] 3:17', title: 'Back button goes to Wishlist instead of previous screen', desc: 'After tapping back from My Cart, the app navigates to an empty Wishlist instead of the product details.', impact: 'Unexpected navigation. Breaks task continuity.', severity: 'MEDIUM' },
      { frame: '[Frame 160-209] 3:19', title: 'Images load blurry during fast scroll', desc: 'Product images appear blurry and load progressively when scrolling quickly through category listings.', impact: 'Poor visual experience on fast interactions.', severity: 'MEDIUM' },
      { frame: '[Frame 34] 0:41', title: 'Corrupted view after dismissing bottom sheet', desc: 'After dismissing a product bottom sheet, the screen briefly shows a corrupted mix of the category page and home screen.', impact: 'Visual artifact during transition.', severity: 'LOW' },
      { frame: '[Frame 165-166] 3:26', title: 'Black bar artifact during scroll', desc: 'A black vertical bar partially renders on the right side of the screen while scrolling through Dental Chew products.', impact: 'Rendering artifact.', severity: 'LOW' }
    ]
  },
  {
    id: 'venture',
    appName: 'Venture App',
    appMeta: 'com.venture.app',
    videoSrc: './screen_recordings/venture.mp4',
    videoDur: '2 min 56 sec',
    pdfName: 'venture.pdf',
    pdfLink: './sample_reports/venture.pdf',
    specs: [
      ['Device', 'samsung SM-J610F'],
      ['OS', 'Android 10'],
      ['Network', 'WIFI'],
      ['RAM', '4.0 GB'],
      ['Screen', '720×1454'],
      ['Tier', 'LOW'],
      ['Location', 'Hubballi, Karnataka'],
      ['Tester', 'Verified Crowd Tester'],
      ['Session', '176s'],
      ['Speed', '1048 Mbps'],
      ['Net Drop', 'No'],
      ['Thermal', 'Severe'],
      ['Free RAM', '971 MB'],
      ['Storage', '14570 MB'],
      ['Battery Saver', 'Off'],
    ],
    repro: [
      'Ensure "Show Taps" is ON in Android Developer Options.',
      'Open the Venture app.',
      'Navigate to the "Watchlist" tab.',
      'Tap on any stock (e.g., ADANIENT).',
      'Attempt to interact with "Sell" or "Buy" buttons, or other locked features.',
      'Observe the repeated appearance of "Complete KYC" modals.',
      'Navigate to the "Spotlight" tab.',
      'Scroll down and tap "View all news" under "In the news".',
      'Tap on any news article. Observe the black screen hang and failure to load content.',
      'Navigate to the hamburger menu > "Equity" > "F&O" > "52W low".',
      'Observe the app crashing to the device home screen.'
    ],
    rootCause: `The tester deviated from the mandatory pre-test setup instruction to "Turn OFF Wi-Fi. You must use your standard Mobile Data (4G/5G)." The device telemetry clearly states "Network: WiFi (1048 Mbps)". The "Developer Mode detected" modal is an explicit app response to the developer option being enabled. The repetitive "Complete KYC" modals suggest inconsistent state management within the application's feature gating. The app freezing on news article loads (black screen for ~8 seconds repeatedly) points to poor error handling, inefficient content loading for external web views, or resource contention. The "Thermals: Severe" telemetry indicates the device was under significant stress. The final unexpected app crash indicates a critical stability issue, potentially a memory leak or an unhandled exception triggered by specific UI interactions.`,
    fixes: [
      'Investigate and unify the KYC onboarding and feature gating logic to prevent repetitive and disruptive modals.',
      'Implement robust error handling and timeouts for loading external web content, such as news articles.',
      'Optimize news article loading and rendering, especially for embedded web views, to prevent prolonged black screens.',
      'Review the application\'s resource management and memory usage to identify potential memory leaks or inefficient processing.',
      'Re-evaluate the "Developer Mode detected" behavior.'
    ],
    issues: [
      {
        frame: '[Frame 146] 2:51',
        title: 'App Unexpectedly Crashes to Home Screen',
        desc: 'After interacting with the "Market Watch" screen, the app unexpectedly closed, and the device\'s home screen was displayed.',
        impact: 'Complete disruption of user activity, loss of current session, and perception of an unstable application.',
        severity: 'CRITICAL',
      },
      {
        frame: '[Frame 101, 127] 1:58, 2:28',
        title: 'Prolonged Black Screen on News Article Load',
        desc: 'After tapping a news item, a loading spinner appeared, then the screen turned completely black for ~8 seconds before the article loaded (or failed to load).',
        impact: 'Severe delay and frozen UI, leading to a poor user experience and failure to load content.',
        severity: 'HIGH',
      },
      {
        frame: '[Frame 29, 32, 35] 0:33, 0:37, 0:40',
        title: 'Repetitive KYC Modals Block Interaction',
        desc: 'Multiple different modals consistently appeared, all demanding "Complete KYC" to proceed with core functionalities.',
        impact: 'Frustrating user experience, blocks interaction with app features, and disrupts the flow repeatedly.',
        severity: 'MEDIUM',
      },
      {
        frame: '[Frame 43] 0:49',
        title: 'Unexpected Navigation Flow to Email Onboarding',
        desc: 'After navigating back to the Watchlist, a spinning Ventura logo appeared. Following this, the app navigated to an "Add your email" screen unexpectedly.',
        impact: 'Delays content display and forces the user into an unexpected KYC/onboarding flow.',
        severity: 'MEDIUM',
      },
      {
        frame: '[Frame 16] 0:18',
        title: '"Developer Mode Detected" Modal Appears',
        desc: 'After dismissing a full-screen KYC overlay, a modal titled "Developer Mode detected" appeared with buttons "Disable Developer Mode" and "Proceed anyway".',
        impact: 'Interrupts user flow and requires additional interaction.',
        severity: 'LOW',
      }
    ]
  },
  {
    id: 'kindlife',
    appName: 'Kindlife',
    appMeta: 'com.kindlife.app',
    videoSrc: './screen_recordings/kindlife.mp4',
    videoDur: '7 min 21 sec',
    pdfName: 'Kindlife.pdf',
    pdfLink: './sample_reports/Kindlife.pdf',
    specs: [
      ['Device', 'samsung SM-J610F'],
      ['OS', 'Android 10'],
      ['Network', 'WIFI'],
      ['RAM', '4.0 GB'],
      ['Screen', '720×1454'],
      ['Tier', 'LOW'],
      ['Location', 'Hubballi, Karnataka'],
      ['Tester', 'Verified Crowd Tester'],
      ['Session', '441s'],
      ['Speed', '1048 Mbps'],
      ['Net Drop', 'No'],
      ['Thermal', 'Severe'],
      ['Free RAM', '971 MB'],
      ['Storage', '14570 MB'],
      ['Battery Saver', 'Off'],
    ],
    repro: [
      'Open the app and sign up or log in.',
      'Browse the home screen and add at least two items to the cart.',
      'Navigate to the cart page.',
      'Click the "Place order" button.',
      'Observe the "Delivery Address" section displaying an error.',
      'Click the "+ Add New Address" button.',
      'Fill in only the "Address Type" and "Select State" fields, leaving other required fields empty.',
      'Dismiss the "Add Address" modal.',
      'Observe that the error persists, and there is no clear way to save an incomplete address or receive validation feedback within the modal itself.'
    ],
    rootCause: `The application exhibits several performance and functional issues: Frequent and prolonged UI freezes/loading states suggest inefficient rendering, unoptimized data fetching, or a bottleneck in the main UI thread. The checkout address flow is critically flawed. The "Add Address" modal does not enforce input validation for required fields upon user interaction, allowing an invalid address state to persist. This indicates missing client-side validation logic integration. The video playback interruption could be due to memory management issues, network stream buffering problems, or unhandled exceptions in the video player component.`,
    fixes: [
      'Implement client-side input validation for all required fields within the "Add Address" modal.',
      'Optimize data fetching and UI rendering for high-traffic screens like "My Account" and search results to reduce prolonged loading times and UI freezes.',
      'Investigate the video player component for potential memory leaks, unhandled exceptions during playback, or issues with network stream handling.',
      'Enhance error handling and user feedback for coupon applications, providing specific reasons why a coupon is not applicable.'
    ],
    issues: [
      {
        frame: '[Frame 179, 184, 204] 5:14, 5:23, 5:59',
        title: 'Critical Checkout Address Blockage',
        desc: 'After clicking "Place order", the checkout screen displays an error. The user attempts to add an address but leaves required fields empty. Upon dismissal, the error reappears without a clear path to save a valid address.',
        impact: 'User is completely blocked from completing the checkout process because a valid shipping address cannot be entered.',
        severity: 'CRITICAL',
      },
      {
        frame: '[Frame 20-35] 0:34-1:00',
        title: 'My Account Screen Freeze (26s)',
        desc: 'The "My Account" screen displays a persistent loading spinner in the center of a blank white screen across these frames. The screen content remains static for approximately 26 seconds.',
        impact: 'User is unable to access their account details, leading to a blocked flow for managing account information.',
        severity: 'HIGH',
      },
      {
        frame: '[Frame 84-89] 2:27-2:35',
        title: 'My Account Screen Frozen (11s)',
        desc: 'The "My Account" screen displays a blank white screen, identical in appearance across frames. The UI is completely frozen and unresponsive.',
        impact: 'User is prevented from interacting with the "My Account" section for a significant duration.',
        severity: 'HIGH',
      },
      {
        frame: '[Frame 127-133] 3:43-3:53',
        title: 'Search Results Loading Spinner Stuck (10s)',
        desc: 'The search results screen displays a persistent loading spinner in the center of a greyed-out screen with search suggestions visible.',
        impact: 'Search results are delayed, causing a sluggish and unresponsive user experience.',
        severity: 'MEDIUM',
      },
      {
        frame: '[Frame 220-221] 6:27',
        title: 'Live Video Playback Interrupted',
        desc: 'During live video playback, the screen turns black and a loading spinner appears. The video player UI shows playback time at "00:00" and "44:03" indicating a restart or re-buffering event.',
        impact: 'Video playback is interrupted, leading to a disruptive user experience.',
        severity: 'MEDIUM',
      },
      {
        frame: '[Frame 226] 6:37',
        title: 'Live Video Background Static Issue',
        desc: 'The live video content is replaced by a static background image of the room behind the screen, while the video player controls remain visible.',
        impact: 'The live video playback unexpectedly terminates or minimizes from the screen.',
        severity: 'MEDIUM',
      }
    ]
  }
];

export default function SampleReportCard() {
  const [activeId, setActiveId] = useState(REPORTS[0].id);
  const [showAll, setShowAll] = useState(false);
  
  const report = REPORTS.find(r => r.id === activeId);
  const visibleIssues = showAll ? report.issues : report.issues.slice(0, 5);

  const critCount = report.issues.filter(i => i.severity === 'CRITICAL').length;
  const highCount = report.issues.filter(i => i.severity === 'HIGH').length;
  const medCount  = report.issues.filter(i => i.severity === 'MEDIUM').length;
  const lowCount  = report.issues.filter(i => i.severity === 'LOW').length;

  return (
    <div className={styles.wrapper}>
      {/* ── Report Tabs ── */}
      <div className={styles.tabs}>
        {REPORTS.map(r => (
          <button 
            key={r.id} 
            className={`${styles.tabBtn} ${activeId === r.id ? styles.tabActive : ''}`}
            onClick={() => { setActiveId(r.id); setShowAll(false); }}
          >
            {r.appName}
          </button>
        ))}
      </div>

      <div className={styles.card}>
        {/* ── Report header ── */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.reportBadge}>AI Bug Analysis</span>
            <h3 className={styles.appName}>{report.appName}</h3>
            <span className={styles.appMeta}>{report.appMeta}</span>
          </div>
          <div className={styles.headerRight}>
            <div className={styles.sevSummary}>
              {critCount > 0 && <span className={styles.sevPill} style={{background: SEV.CRITICAL.bg, color: SEV.CRITICAL.color, borderColor: SEV.CRITICAL.border}}>{critCount} Critical</span>}
              {highCount > 0 && <span className={styles.sevPill} style={{background: SEV.HIGH.bg, color: SEV.HIGH.color, borderColor: SEV.HIGH.border}}>{highCount} High</span>}
              {medCount > 0 && <span className={styles.sevPill} style={{background: SEV.MEDIUM.bg, color: SEV.MEDIUM.color, borderColor: SEV.MEDIUM.border}}>{medCount} Medium</span>}
              {lowCount > 0 && <span className={styles.sevPill} style={{background: SEV.LOW.bg, color: SEV.LOW.color, borderColor: SEV.LOW.border}}>{lowCount} Low</span>}
            </div>
          </div>
        </div>

        {/* ── Screen Recording + PDF ── */}
        <div className={styles.section}>
          <div className={styles.mediaRow}>
            <div className={styles.videoWrap}>
              <div className={styles.sectionLabel}>Screen Recording</div>
              <video
                key={report.videoSrc}
                className={styles.video}
                controls
                preload="metadata"
                poster=""
                playsInline
              >
                <source src={report.videoSrc} type="video/mp4" />
              </video>
              <div className={styles.videoMeta}>
                <span>{report.videoDur}</span>
                <span>{report.specs.find(s => s[0] === 'Session')[1]} session · {report.specs.find(s => s[0] === 'Device')[1]}</span>
              </div>
            </div>
            <div className={styles.pdfWrap}>
              <div className={styles.sectionLabel}>Full Report</div>
              <div className={styles.pdfCard}>
                <svg className={styles.pdfIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
                <div className={styles.pdfInfo}>
                  <span className={styles.pdfName}>{report.pdfName}</span>
                  <span className={styles.pdfMeta}>AI-generated analysis with full evidence</span>
                </div>
                <a
                  href={report.pdfLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.pdfBtn}
                >
                  View report
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* ── Device specs grid ── */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Session Specs</div>
          <div className={styles.specsGrid}>
            {report.specs.map(([label, value]) => (
              <div key={label} className={styles.specItem}>
                <span className={styles.specLabel}>{label}</span>
                <span className={`${styles.specValue} ${label === 'Thermal' ? styles.specDanger : ''}`}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Issues list ── */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Observed Issues ({report.issues.length})</div>
          <div className={styles.issuesList}>
            {visibleIssues.map((issue, i) => {
              const sev = SEV[issue.severity];
              return (
                <div key={i} className={styles.issueCard}>
                  <div className={styles.issueTop}>
                    <span className={styles.issueFrame}>{issue.frame}</span>
                    <span className={styles.issueSev} style={{background: sev.bg, color: sev.color, borderColor: sev.border}}>{issue.severity}</span>
                  </div>
                  <div className={styles.issueTitle}>{issue.title}</div>
                  <div className={styles.issueDesc}>{issue.desc}</div>
                  <div className={styles.issueImpact}>
                    <span className={styles.impactLabel}>Impact</span> {issue.impact}
                  </div>
                </div>
              );
            })}
          </div>
          {report.issues.length > 5 && (
            <button className={styles.showMoreBtn} onClick={() => setShowAll(!showAll)}>
              {showAll ? 'Show fewer issues' : `Show all ${report.issues.length} issues`}
            </button>
          )}
        </div>

        {/* ── Reproduction steps ── */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Reproduction Steps</div>
          <ol className={styles.reproList}>
            {report.repro.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </div>

        {/* ── Root cause ── */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Technical Root Cause</div>
          <div className={styles.rootCause}>{report.rootCause}</div>
        </div>

        {/* ── Recommended fixes ── */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Recommended Fixes</div>
          <ul className={styles.fixesList}>
            {report.fixes.map((fix, i) => (
              <li key={i}>{fix}</li>
            ))}
          </ul>
        </div>

      </div>
    </div>
  );
}
